import { useEffect, useMemo, useRef, useState } from 'react'
import styles from './MergeSkillModal.module.css'

/** Select the existing active skill that will receive a candidate skill's evidence. */
export default function MergeSkillModal({
  skillName,
  skills = [],
  busy = false,
  error = '',
  onMerge,
  onCancel,
}) {
  const options = useMemo(
    () => [...skills].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })),
    [skills],
  )
  const [targetSkillId, setTargetSkillId] = useState(() => String(options[0]?.id ?? ''))
  const selectRef = useRef(null)

  useEffect(() => {
    selectRef.current?.focus()
  }, [])

  const canMerge = targetSkillId !== '' && !busy

  return (
    <div className={styles.backdrop}>
      <div className={styles.dialog} role="dialog" aria-modal="true" aria-label="Merge skill candidate">
        <div className={styles.header}>
          <span className={styles.eyebrow}>Merge Skill</span>
          <h2 className={styles.skillName}>{skillName}</h2>
        </div>
        <p className={styles.message}>
          Choose an existing skill. All evidence cases from this candidate will be
          connected to that skill, and this candidate will be removed.
        </p>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>Merge into</span>
          <select
            ref={selectRef}
            className={styles.select}
            value={targetSkillId}
            onChange={event => setTargetSkillId(event.target.value)}
            disabled={busy || options.length === 0}
          >
            {options.length === 0
              ? <option value="">No existing skills available</option>
              : options.map(skill => (
                  <option key={skill.id} value={skill.id}>
                    {skill.name}{skill.type ? ` (${skill.type})` : ''}
                  </option>
                ))}
          </select>
        </label>

        {error && <p className={styles.error}>{error}</p>}

        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.mergeBtn}
            onClick={() => canMerge && onMerge(Number(targetSkillId))}
            disabled={!canMerge}
          >
            {busy ? 'Merging…' : 'Merge Skill'}
          </button>
        </div>
      </div>
    </div>
  )
}
